/** URL oficial do script do Connection SDK da Z-API. */
const ZAPI_SDK_SCRIPT_SRC = 'https://app.z-api.io/sdk.js';
const ZAPI_SDK_SCRIPT_ID = 'zapi-connector-sdk-script';

/** Tempo máximo de espera pelo carregamento do script antes de desistir. */
const ZAPI_SDK_LOAD_TIMEOUT_MS = 15000;

/** Cache da Promise em andamento — garante idempotência entre chamadas concorrentes. */
let zApiConnectorSdkPromise: Promise<void> | null = null;

/**
 * Carrega sob demanda o Connection SDK da Z-API (modal alternativo ao QR Code tradicional).
 *
 * `<script src="https://app.z-api.io/sdk.js" async>`, conforme a referência oficial da Z-API
 * ("2. Load the SDK"): https://developer.z-api.io/en/partner/sdk-connector — expõe
 * `window.ZAPIConnector` globalmente.
 *
 * Idempotente: se já houver um carregamento em andamento, todas as chamadas reaproveitam a
 * mesma Promise (o script nunca é injetado mais de uma vez); se o SDK já estiver carregado
 * (`window.ZAPIConnector` presente), resolve imediatamente sem tocar no DOM. Em caso de falha
 * (erro de rede ou timeout), a Promise em cache é descartada para permitir uma nova tentativa
 * na próxima chamada.
 */
export function loadZApiConnectorSdk(): Promise<void> {
	if (typeof window === 'undefined') {
		return Promise.reject(new Error('loadZApiConnectorSdk só pode ser chamado no navegador.'));
	}

	if (window.ZAPIConnector) {
		return Promise.resolve();
	}

	if (zApiConnectorSdkPromise) {
		return zApiConnectorSdkPromise;
	}

	zApiConnectorSdkPromise = new Promise<void>((resolve, reject) => {
		const script = document.createElement('script');
		script.id = ZAPI_SDK_SCRIPT_ID;
		script.src = ZAPI_SDK_SCRIPT_SRC;
		script.async = true;

		function clearListeners() {
			window.clearTimeout(timeoutId);
			script.removeEventListener('load', handleLoad);
			script.removeEventListener('error', handleError);
		}

		// Remove a tag do DOM apenas nos caminhos de falha — em caso de sucesso ela deve
		// permanecer no body normalmente. Evita acumular <script> órfãos a cada retry.
		function removeScript() {
			script.parentNode?.removeChild(script);
		}

		function handleLoad() {
			clearListeners();
			if (window.ZAPIConnector) {
				resolve();
			} else {
				removeScript();
				reject(
					new Error(
						'O script do SDK Connector da Z-API carregou, mas window.ZAPIConnector não foi exposto.',
					),
				);
			}
		}

		function handleError() {
			clearListeners();
			removeScript();
			reject(
				new Error(`Falha de rede ao carregar o SDK Connector da Z-API (${ZAPI_SDK_SCRIPT_SRC}).`),
			);
		}

		const timeoutId = window.setTimeout(() => {
			clearListeners();
			removeScript();
			reject(
				new Error(
					`Tempo limite de ${ZAPI_SDK_LOAD_TIMEOUT_MS}ms excedido ao carregar o SDK Connector da Z-API (${ZAPI_SDK_SCRIPT_SRC}).`,
				),
			);
		}, ZAPI_SDK_LOAD_TIMEOUT_MS);

		script.addEventListener('load', handleLoad);
		script.addEventListener('error', handleError);

		document.body.appendChild(script);
	});

	zApiConnectorSdkPromise.catch(() => {
		zApiConnectorSdkPromise = null;
	});

	return zApiConnectorSdkPromise;
}
